import * as React from 'react';
import {
    useState,
    useEffect,
    useCallback,
    useRef,
    useReducer,
    useLayoutEffect,
    useImperativeHandle,
    forwardRef,
} from 'react';
import * as Fuse from 'fuse.js';
import cx from 'classnames';
import _ from 'lodash';

import TextField from 'material-ui/TextField';
import MenuItem from 'material-ui/MenuItem';
import RaisedButton from 'material-ui/RaisedButton';
import IconMenu from 'material-ui/IconMenu';
import IconButton from 'material-ui/IconButton';
import CircularProgress from 'material-ui/CircularProgress';

import EditIcon from 'material-ui/svg-icons/image/edit';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import FileDownload from 'material-ui/svg-icons/file/file-download';
import DeleteSweep from 'material-ui/svg-icons/content/delete-sweep';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';
import ChevronLeft from 'material-ui/svg-icons/navigation/chevron-left';
import ChevronRight from 'material-ui/svg-icons/navigation/chevron-right';

import AwaitableBtn from 'components/Base/AwaitableBtn';

import { 
    loadDefFile, 
    loadLstFile, 
    buildLst, 
    getEmptyFeatures, 
    getColumns, 
    saveDef, 
    validator, 
    removeDuplicates, 
    calculateDuplicateEntries, 
} from './service';

import * as gazetteerService from 'services/gazetters';

import helpers from 'helpers/index';
import { execute, UserPerms } from 'store/configureStore';

import './styles.scss';
import { withGazetterContentSave } from './HOC';
import { showNotification } from 'helpers';

class DynamicKey {
    KEY = Symbol('dynamic key');

    /**
     * @private
     */
    value = 1;

    get next() { this.value += 1; return this.value; }
}

/** Frozen leading strip + feature column width; keep in sync with styles.scss */
const LST_CONTROLS_COL_PX = 48;
const LST_VALUE_COL_PX = 252;
const LST_STICKY_LEADING_PX = LST_CONTROLS_COL_PX + LST_VALUE_COL_PX;
const LST_FEATURE_COL_PX = 150;

function lstTableFeaturesWidth(featureColumnCount) {
    return featureColumnCount * LST_FEATURE_COL_PX;
}

function lstTableTotalWidth(featureColumnCount) {
    return LST_STICKY_LEADING_PX + lstTableFeaturesWidth(featureColumnCount);
}

const LST_ROW_HEIGHT = 35;
const LST_VIRTUAL_OVERSCAN = 10;

/**
 * Fixed row height + spacer-based windowing (no transform/absolute on rows) so
 * position:sticky works for a frozen first column inside one native scroll container.
 */
const GazetteerLstVirtualTable = forwardRef(function GazetteerLstVirtualTableInner(props, ref) {
    const {
        rowCount,
        rowHeight = LST_ROW_HEIGHT,
        /** Must match sticky header row height in CSS (.lst-header-row) */
        headerHeight = LST_ROW_HEIGHT,
        totalWidth,
        listVersionKey,
        /** When this changes (e.g. different .lst file), scroll to top; filter/edits do not change it. */
        listDocumentKey,
        headerRow,
        renderRow,
    } = props;

    const scrollRef = useRef(null);
    const [range, setRange] = useState({ start: 0, end: 0 });

    const clampRange = useCallback(() => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }
        const st = el.scrollTop;
        const ch = el.clientHeight || 800;
        if (rowCount < 1) {
            setRange((r) => (r.start === 0 && r.end === -1 ? r : { start: 0, end: -1 }));
            return;
        }
        // First data row starts at scrollTop === headerHeight, not 0 (sticky header occupies top of content).
        const bodyTop = st - headerHeight;
        const start = Math.max(0, Math.floor(bodyTop / rowHeight) - LST_VIRTUAL_OVERSCAN);
        const end = Math.min(
            rowCount - 1,
            Math.ceil((bodyTop + ch) / rowHeight) - 1 + LST_VIRTUAL_OVERSCAN,
        );
        setRange((r) => (r.start === start && r.end === end ? r : { start, end }));
    }, [rowCount, rowHeight, headerHeight]);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (el) {
            el.scrollTop = 0;
        }
    }, [listDocumentKey]);

    useLayoutEffect(() => {
        clampRange();
    }, [listVersionKey, rowCount, clampRange]);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }
        const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
        if (el.scrollTop > maxTop) {
            el.scrollTop = maxTop;
        }
        clampRange();
    }, [rowCount, listVersionKey, clampRange]);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const ro = new ResizeObserver(() => clampRange());
        ro.observe(el);
        return () => ro.disconnect();
    }, [clampRange]);

    useImperativeHandle(ref, () => ({
        scrollToRow(index) {
            const el = scrollRef.current;
            if (!el || rowCount < 1) {
                return;
            }
            const idx = Math.min(Math.max(0, index), rowCount - 1);
            el.scrollTop = headerHeight + (idx * rowHeight);
        },
    }), [rowCount, rowHeight, headerHeight]);

    const onScroll = useCallback(() => {
        clampRange();
    }, [clampRange]);

    let displayStart = 0;
    let displayEnd = -1;
    if (rowCount > 0) {
        displayEnd = Math.min(range.end, rowCount - 1);
        displayStart = Math.max(0, Math.min(range.start, displayEnd));
    }

    const topSpacer = rowCount < 1 ? 0 : displayStart * rowHeight;
    const bottomSpacer = rowCount < 1 || displayEnd < 0 ? 0 : (rowCount - 1 - displayEnd) * rowHeight;

    const rows = [];
    if (rowCount > 0 && displayEnd >= displayStart) {
        for (let i = displayStart; i <= displayEnd; i += 1) {
            rows.push(renderRow(i));
        }
    }

    return (
        <div ref={scrollRef} className="lst-virtual-scroll" onScroll={onScroll}>
            <div
                className="lst-virtual-inner"
                style={{ width: totalWidth, minWidth: totalWidth }}
            >
                <div
                    className="lst-header-row lst-header-row--virtual"
                    style={{ width: totalWidth, minWidth: totalWidth }}
                >
                    {headerRow}
                </div>
                <div className="lst-virtual-rows">
                    {topSpacer > 0 ? (
                        <div className="lst-virtual-spacer" style={{ height: topSpacer }} aria-hidden />
                    ) : null}
                    {rows}
                    {bottomSpacer > 0 ? (
                        <div className="lst-virtual-spacer" style={{ height: bottomSpacer }} aria-hidden />
                    ) : null}
                </div>
            </div>
        </div>
    );
});

/** Spinner diameter = 25% of overlay’s smaller side; updates on resize. */
function GazetteerLstLoadingOverlay({ active }) {
    const overlayRef = useRef(null);
    const [progressSize, setProgressSize] = useState(48);

    useLayoutEffect(() => {
        if (!active) {
            return undefined;
        }
        const el = overlayRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const update = () => {
            const { width, height } = el.getBoundingClientRect();
            const side = Math.min(width, height) * 0.25;
            const next = Math.max(24, Math.round(side));
            setProgressSize((prev) => (prev === next ? prev : next));
        };
        update();
        const ro = new ResizeObserver(() => {
            requestAnimationFrame(update);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [active]);

    if (!active) {
        return null;
    }

    const thickness = Math.max(2, Math.min(8, progressSize * 0.085));

    return (
        <div
            ref={overlayRef}
            className="lst-loading-overlay"
            aria-busy="true"
            aria-live="polite"
        >
            <CircularProgress size={progressSize} thickness={thickness} />
        </div>
    );
}

const GazetteerEditorPage = ({
    saveContentMutation,
    isSavingContent,
    isSaveContentSuccess,
}) => {
    const [defData, setDefData] = useState([]);
    const [defView, setDefView] = useState([]);
    const [defVersion, setDefVersion] = useState(undefined);
    const [selectedLst, setSelectedLst] = useState(undefined);
    const [defIndex, setDefIndex] = useState(new Map());
    const lstData = useRef();
    const lstView = useRef();
    const [lstIndex, setLstIndex] = useState(new Map());
    const [lstDuplicatesCount, setLstDuplicatesCount] = useState(0);
    const [selectedDefKey, setSelectedDefKey] = useState(undefined);
    const [selectedLstVersion, setSelectedLstVersion] = useState(undefined);
    /** When true, DEF column is narrow (col-xs-2–equivalent) and index table uses compact rows. */
    const [defPaneNarrow, setDefPaneNarrow] = useState(false);
    const [isLstLoading, setIsLstLoading] = useState(false);

    const columns = useRef(new Set());
    const lstLocalVersion = useRef(0);
    const isNewFeatureNameValid = useRef(true);

    const dKey = useRef(new DynamicKey()).current;
    const defList = useRef(null);
    const lstList = useRef(null);
    const featureNameRef = useRef(null);
    const invalidDefRows = useRef(new Set());
    const invalidRows = useRef(new Set());
    const originalLstData = useRef([]);
    const originalLstIndex = useRef(new Map());
    const gazetteersChanged = useRef([]);
    const originalColumns = useRef(new Set());

    // hook to replicate this.forceUpdate() to preserve the original component's logic
    const [, forceUpdate] = useReducer(x => x + 1, 0);

    const toggleDefPaneNarrow = useCallback(() => {
        setDefPaneNarrow((n) => !n);
    }, []);

    const prepareData = useCallback((items) => {
        const map = new Map();
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item[dKey.KEY]) {
                item[dKey.KEY] = dKey.next;
            }
            map.set(item[dKey.KEY], item);
        }
        return map;
    }, [dKey]);

    useEffect(() => {
        const init = async () => {
            const [, data] = await execute(loadDefFile);
            setDefIndex(prepareData(data));
            setDefData(data);
            setDefView([...data]);
        };
        init();
        return () => {
            gazetteersChanged.current = [];
        };
    }, [prepareData]);


    const recalculateRowChanges = useCallback((indexOverride) => {
        const currentChanges = gazetteersChanged.current;
        const renames = currentChanges.filter(c => c.status === 'renameFeature');
        const newRowChanges = [];

        // 1. calculate 'new' and 'replace' changes by iterating current data
        lstData.current.forEach((currentItem) => {
            const key = currentItem[dKey.KEY];
            const originalItem = originalLstIndex.current.get(key);

            if (!originalItem) { // this is a new row
                const features = _.cloneDeep(currentItem);
                delete features[dKey.KEY];
                delete features.$value;
                newRowChanges.push({
                    key, status: 'new', value: currentItem.$value, lastFeatures: features, 
                });
                return;
            }

            // for existing rows, "un-project" the current item to compare with the original
            const unprojectedCurrent = _.cloneDeep(currentItem);
            [...renames].reverse().forEach((rename) => {
                if (unprojectedCurrent.hasOwnProperty(rename.value)) {
                    unprojectedCurrent[rename.oldValue] = unprojectedCurrent[rename.value];
                    delete unprojectedCurrent[rename.value];
                }
            });

            const allKeys = new Set([...Object.keys(originalItem), ...Object.keys(unprojectedCurrent)]);
            const normalizedOriginal = {};
            const normalizedCurrent = {};

            allKeys.forEach((key) => {
                const originalValue = originalItem[key];
                const currentValue = unprojectedCurrent[key];
                const isNewKey = !originalItem.hasOwnProperty(key);

                if (isNewKey && (currentValue === null || currentValue === undefined || currentValue === '')) {
                    return;
                }
                normalizedOriginal[key] = (originalValue === null || originalValue === undefined) ? '' : originalValue;
                normalizedCurrent[key] = (currentValue === null || currentValue === undefined) ? '' : currentValue;
            });

            if (!_.isEqual(normalizedCurrent, normalizedOriginal)) {
                const features = {};
                columns.current.forEach((colName) => {
                    if (currentItem.hasOwnProperty(colName)) {
                        features[colName] = currentItem[colName];
                    }
                });
                if (currentItem.$value === originalItem.$value) {
                    newRowChanges.push({ 
                        key, status: 'replace', value: currentItem.$value, lastFeatures: features, 
                    });
                } else {
                    newRowChanges.push({ 
                        key, status: 'replace', value: currentItem.$value, oldValue: originalItem.$value, lastFeatures: features, 
                    });
                }
            }
        });

        // 2. calculate 'remove' changes by iterating original data
        originalLstData.current.forEach((originalItem) => {
            const key = originalItem[dKey.KEY];
            const indexToUse = indexOverride || lstIndex;
            if (!indexToUse.get(key)) {
                newRowChanges.push({ key, status: 'remove', value: originalItem.$value });
            }
        });

        // 3. combine rename records with the newly calculated row changes
        gazetteersChanged.current = [...renames, ...newRowChanges];
        forceUpdate();
    }, [dKey.KEY, lstIndex]);


    const _updateGazetteerChanges = useCallback(({ type, payload }) => {
        const newChanges = gazetteersChanged.current;
        
        if (type === 'FEATURE_RENAMED') {
            const { oldName, newName } = payload;

            newChanges.forEach((change) => {
                if (change.lastFeatures && change.lastFeatures.hasOwnProperty(oldName)) {
                    change.lastFeatures[newName] = change.lastFeatures[oldName];
                    delete change.lastFeatures[oldName];
                }
            });
            
            const existingRenameIndex = newChanges.findIndex(c => c.status === 'renameFeature' && c.value === oldName);

            if (existingRenameIndex > -1) {
                const existingRename = newChanges[existingRenameIndex];
                if (existingRename.oldValue === newName) {
                    newChanges.splice(existingRenameIndex, 1);
                } else {
                    existingRename.value = newName;
                }
            } else if (oldName !== newName) {
                newChanges.push({ status: 'renameFeature', value: newName, oldValue: oldName });
            }
        }
    }, []);
    
    const onDownloadLst = useCallback(() => {
        const content = buildLst(lstData.current, columns.current);
        const url = window.URL.createObjectURL(new window.Blob([content], { type: 'text/plain' }));
        const element = document.createElement('a');
        element.setAttribute('href', url);
        element.setAttribute('download', selectedLst);
        element.click();
        window.URL.revokeObjectURL(url);
    }, [selectedLst]);


    useEffect(() => {
        if (isSaveContentSuccess) {
            originalLstData.current = _.cloneDeep(lstData.current);
            originalColumns.current = new Set(columns.current);
            originalLstIndex.current = prepareData(originalLstData.current);
            gazetteersChanged.current = [];
            forceUpdate();
        }
    }, [isSaveContentSuccess, prepareData]);

    const onSaveLst = useCallback(async () => {
        if (!gazetteersChanged.current.length) return;

        const emptyFeatures = getEmptyFeatures(lstData.current, columns.current);
        if (emptyFeatures.length > 0) {
            showNotification(
                `Gazetteer ${selectedLst} has empty features: ${emptyFeatures.join(', ')}. \nRemove them or fill with at least one value per feature.`,
                'error',
            );
            return;
        }

        await saveContentMutation({
            gazetterName: selectedLst,
            values: gazetteersChanged.current,
        });
    }, [selectedLst, saveContentMutation]);

    const onSaveDef = useCallback(async () => {
        if (invalidDefRows.current.size > 0) {
            showNotification(
                `File has ${invalidDefRows.current.size} error(s)! Fix them before saving def.`,
                'error',
            );
            return;
        }

        const [_newVersion, data] = await execute(saveDef, defVersion, defData);
        
        setDefIndex(prepareData(data));
        setDefData(data);
        setDefView([...data]);
        setDefVersion(_newVersion);
    }, [defData, defVersion, prepareData]);

    const loadLst = useCallback(async (defEntry) => {
        gazetteersChanged.current = [];
        invalidRows.current.clear();
        
        let version;
        let items = [{ [dKey.KEY]: dKey.next }];
        try {
            [version, items] = await execute(loadLstFile, defEntry.fileName);
        } catch (err) {
            // file not found
        }

        const liveIndex = prepareData(items);

        originalLstData.current = _.cloneDeep(items);
        originalLstIndex.current = prepareData(originalLstData.current);
        
        const duplicatesCount = calculateDuplicateEntries(items);
        
        const newColumns = getColumns(items);
        newColumns.delete('$value');
        columns.current = newColumns;
        originalColumns.current = new Set(columns.current);

        lstLocalVersion.current = 0;
        lstData.current = items;
        lstView.current = [...items];
        setLstIndex(liveIndex);
        setLstDuplicatesCount(duplicatesCount);
        setSelectedDefKey(defEntry[dKey.KEY]);
        setSelectedLstVersion(version);
        setSelectedLst(defEntry.fileName);
        gazetteersChanged.current = [];
    }, [dKey, prepareData]);

    const loadLstWithOverlay = useCallback(async (defEntry) => {
        if (!defEntry) {
            return;
        }
        setIsLstLoading(true);
        try {
            await loadLst(defEntry);
        } finally {
            setIsLstLoading(false);
        }
    }, [loadLst]);

    const onDedupLst = useCallback(async () => {
        await execute(removeDuplicates, selectedLst);
        const defEntry = defIndex.get(selectedDefKey);
        if (defEntry) {
            await loadLstWithOverlay(defEntry);
            gazetteersChanged.current = [];
        }
    }, [defIndex, selectedDefKey, selectedLst, loadLstWithOverlay]);

    const getValKey = useCallback((field, key) => `${field}:${key}`, []);

    const addInvalidMark = useCallback((invRows, field = '', key = '') => {
        const value = getValKey(field, key);
        invRows.add(value);
    }, [getValKey]);

    const removeInvalidMark = useCallback((invRows, field = '', key = '') => {
        const escapedField = helpers.escapeRegExp(field);
        const escapedKey = helpers.escapeRegExp(key);
        const keyReg = `${escapedField}:${escapedKey}`;
        invRows.forEach((invalidRow) => {
            if (invalidRow.match(keyReg)) {
                invRows.delete(invalidRow);
            }
        });
    }, []);

    const onDefCellChanged = useCallback((e, value) => {
        const el = e.target;
        if (value === undefined) value = el.value;
        const field = el.dataset.field;
        const index = parseInt(el.closest('[data-key]').dataset.key);

        if (validator.defValue(value)) {
            el.classList.remove('invalid');
            removeInvalidMark(invalidDefRows.current, field, index.toString());
        } else {
            el.classList.add('invalid');
            addInvalidMark(invalidDefRows.current, field, index.toString());
        }

        const item = defIndex.get(index);
        if (item) {
            item[field] = value;
        }
    }, [defIndex, addInvalidMark, removeInvalidMark]);

    const onLstCellChanged = useCallback((e, value) => {
        const el = e.target;
        if (value === undefined) value = el.value;
        const field = el.dataset.field;
        const key = parseInt(el.closest('[data-key]').dataset.key);

        const validatorFn = field === '$value' ? validator.lstValue : validator.lstFeature;
        if (validatorFn(value)) {
            el.classList.remove('invalid');
            removeInvalidMark(invalidRows.current, field, key.toString());
        } else {
            el.classList.add('invalid');
            addInvalidMark(invalidRows.current, field, key.toString());
        }

        const item = lstIndex.get(key);
        if (item) {
            item[field] = value;
            // no need to setLstData, just trigger recalculation
            recalculateRowChanges();
        }
    }, [lstIndex, recalculateRowChanges, addInvalidMark, removeInvalidMark]);

    const onLoadLst = useCallback((e) => {
        e.stopPropagation();
        const row = e.currentTarget.closest('[data-key]');
        if (!row) {
            return;
        }
        const key = parseInt(row.dataset.key, 10);
        const defEntry = defIndex.get(key);
        if (!defEntry) {
            return;
        }
        setDefPaneNarrow(true);
        void loadLstWithOverlay(defEntry);
    }, [defIndex, loadLstWithOverlay]);

    const onCompactDefRowActivate = useCallback((e) => {
        if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') {
            return;
        }
        if (e.type === 'keydown') {
            e.preventDefault();
        }
        const key = parseInt(e.currentTarget.dataset.key, 10);
        const defEntry = defIndex.get(key);
        if (!defEntry) {
            return;
        }
        setDefPaneNarrow(true);
        void loadLstWithOverlay(defEntry);
    }, [defIndex, loadLstWithOverlay]);

    const onAddFeature = useCallback(() => {
        const textEdit = featureNameRef.current;
        if (columns.current && textEdit) {
            const featureName = textEdit.getValue().trim();

            isNewFeatureNameValid.current = validator.lstFeature(featureName);
            if (!isNewFeatureNameValid.current || featureName === '') {
                forceUpdate();
                return;
            }

            textEdit.input.value = '';
            if (!featureName) {
                return alert('You should write valid feature name first');
            }
            columns.current.add(featureName);
            lstLocalVersion.current++;
            forceUpdate();
        }
    }, []);

    const onAddLstEntry = useCallback(() => {
        const newRow = { [dKey.KEY]: dKey.next, $value: '' };
        lstData.current.push(newRow);
        lstView.current = [...lstData.current];
        const newIndex = prepareData(lstData.current);
        setLstIndex(newIndex);
        
        recalculateRowChanges(newIndex);

        setTimeout(() => {
            if (lstList.current) {
                lstList.current.scrollToRow(lstData.current.length);
            }
        });
    }, [dKey, prepareData, recalculateRowChanges]);

    const onDeleteLstEntry = useCallback((e) => {
        const el = e.target;
        const key = parseInt(el.closest('[data-key]').dataset.key);

        removeInvalidMark(invalidRows.current, '', key.toString());

        const itemToRemove = lstIndex.get(key);
        lstData.current = lstData.current.filter(item => item !== itemToRemove);
        lstView.current = lstData.current.filter(item => item !== itemToRemove);
        const newIndex = prepareData(lstData.current);
        setLstIndex(newIndex);
        
        lstLocalVersion.current++;
        recalculateRowChanges(newIndex);
    }, [prepareData, removeInvalidMark, recalculateRowChanges]);

    const onAddDefRow = useCallback(() => {
        const row = {
            [dKey.KEY]: dKey.next, fileName: '', majorType: '', minorType: '', language: '', local: true, 
        };
        defData.push(row);
        defIndex.set(row[dKey.KEY], row);
        setDefView([...defData]);
        forceUpdate();
        setTimeout(() => {
            if (defList.current) {
                defList.current.scrollBy(0, Number.MAX_SAFE_INTEGER);
            }
        });
    }, [dKey, defData, defIndex]);

    const onDeleteDefEntry = useCallback((e) => {
        const el = e.target;
        const index = parseInt(el.closest('[data-key]').dataset.key);
        const itemToDelete = defIndex.get(index);
        if (!confirm(`Are you sure to remove ${itemToDelete.fileName}`)) {
            return;
        }
        const newDefData = defData.filter(item => item !== itemToDelete);
        setDefData(newDefData);
        setDefView([...newDefData]);
        defIndex.delete(index);
        forceUpdate();
    }, [defData, defIndex]);

    const updateInvalidMark = useCallback((invRows, oldField, newField) => {
        const escaped = helpers.escapeRegExp(oldField);
        const keyReg = `${escaped}:([^:]+)`;
        invRows.forEach((invalidRow) => {
            const match = invalidRow.match(keyReg);
            if (match) {
                invRows.delete(invalidRow);
                invRows.add(getValKey(newField, match[1]));
            }
        });
    }, [getValKey]);

    const onLstFeatureChanged = useCallback((oldValue, newValue, remove = false) => {
        if (remove) {
            if (!originalColumns.current.has(oldValue)) { // new feature being deleted
                // 1. remove the feature from all existing change records
                gazetteersChanged.current.forEach((change) => {
                    if (change.lastFeatures && change.lastFeatures.hasOwnProperty(oldValue)) {
                        delete change.lastFeatures[oldValue];
                    }
                });

                // 2. remove the column from the live data model
                columns.current.delete(oldValue);
                lstData.current.forEach((item) => {
                    delete item[oldValue];
                });

                // 3. re-evaluate all changes to see if any are now void
                const stillChanged = gazetteersChanged.current.filter((change) => {
                    if (change.status === 'remove' || change.status === 'renameFeature') {
                        return true;
                    }
                    const originalItem = originalLstIndex.current.get(change.key);
                    const currentItem = lstData.current.find(item => item[dKey.KEY] === change.key);
                    
                    if (originalItem && currentItem) {
                        const cleanCurrent = _.cloneDeep(currentItem);
                        delete cleanCurrent[dKey.KEY];
                        delete cleanCurrent.$value;
                        
                        const cleanOriginal = _.cloneDeep(originalItem);
                        delete cleanOriginal[dKey.KEY];
                        delete cleanOriginal.$value;
                        
                        return !_.isEqual(cleanCurrent, cleanOriginal);
                    }
                    return change.status === 'new' && currentItem;
                });
                gazetteersChanged.current = stillChanged;
            } else {
                // this is an original feature -> remove it visually
                columns.current.delete(oldValue);
                gazetteersChanged.current.push({ status: 'deleteFeature', value: oldValue });
            }

            lstLocalVersion.current++;
            removeInvalidMark(invalidRows.current, oldValue);
            forceUpdate();
            return;
        }

        if (!newValue || !columns.current.has(oldValue) || columns.current.has(newValue)) {
            forceUpdate();
            return;
        }

        // 1. mutate the live data structures FIRST
        updateInvalidMark(invalidRows.current, oldValue, newValue);
        const cols = Array.from(columns.current);
        const index = cols.indexOf(oldValue);
        cols[index] = newValue;
        columns.current = new Set(cols);

        lstData.current.forEach((item) => {
            item[newValue] = item[oldValue];
            delete item[oldValue];
        });

        // 2. update the rename records based on the action
        const isOriginalFeature = originalColumns.current.has(oldValue);
        const isPreviouslyRenamedFeature = gazetteersChanged.current.some(c => c.status === 'renameFeature' && c.value === oldValue);

        if (isOriginalFeature || isPreviouslyRenamedFeature) {
            _updateGazetteerChanges({ type: 'FEATURE_RENAMED', payload: { oldName: oldValue, newName: newValue } });
        }
        
        // 3. with all data consistent, recalculate all row changes
        recalculateRowChanges();
        
        // 4. finally, trigger the re-render
        lstLocalVersion.current++;
        forceUpdate();
    }, [recalculateRowChanges, removeInvalidMark, updateInvalidMark, dKey.KEY, _updateGazetteerChanges]);

    const onFilterDef = useCallback(_.debounce((e, text) => {
        if (text.length === 0) {
            setDefView(defData);
            return;
        }
        const fuse = new Fuse(defData, {
            keys: ['fileName', 'majorType', 'minorType'],
            shouldSort: false,
            threshold: 0.1,
            distance: 320,
            maxPatternLength: 32,
        });
        const items = fuse.search(text);
        setDefView(items);
    }, 150, { leading: true }), [defData]);

    const find_custom = useCallback((items, fields, text) => {
        const pattern = new RegExp(_.escapeRegExp(text), 'i');
        return items.filter(i => fields.some(f => pattern.test(i[f] || '')));
    }, []);

    const onFilterLst = useCallback(
        _.debounce((e, text) => {
            if (text.length === 0) {
                lstView.current = lstData.current;
                lstLocalVersion.current++;
                forceUpdate();
                return;
            }
            const items = find_custom(lstData.current, ['$value', ...Array.from(columns.current, col => col)], text);
            lstView.current = items;
            lstLocalVersion.current++;
            forceUpdate();
        }, 150, { leading: true }),
        [find_custom],
    );

    const renderDefRow = useCallback((item) => {
        const key = item[dKey.KEY];
        return (
            <tr key={key} data-key={key} className={cx({ loaded: key === selectedDefKey })}>
                <td className="cell readonly">
                    <input disabled={!item.local} defaultValue={item.fileName} data-field="fileName" onChange={onDefCellChanged} />
                </td>
                <td className="cell">
                    <input defaultValue={item.majorType} data-field="majorType" onChange={onDefCellChanged} />
                </td>
                <td className="cell">
                    <input defaultValue={item.minorType} data-field="minorType" onChange={onDefCellChanged} />
                </td>
                <td>
                    <select data-field="$target" defaultValue={item.$target} onChange={onDefCellChanged}>
                        <option value="common">common</option>
                        <option value="cargo">cargo</option>
                        <option value="vessel">vessel</option>
                    </select>
                </td>
                <td className="controls">
                    <EditIcon className="edit" onClick={onLoadLst} />
                    <DeleteIcon className="delete" onClick={onDeleteDefEntry} />
                </td>
            </tr>
        );
    }, [selectedDefKey, onDefCellChanged, onLoadLst, onDeleteDefEntry, dKey.KEY]);

    const renderDefRowCompact = useCallback((item) => {
        const key = item[dKey.KEY];
        return (
            <tr
                key={key}
                data-key={key}
                role="button"
                tabIndex={0}
                className={cx('def-row-compact', { loaded: key === selectedDefKey })}
                onClick={onCompactDefRowActivate}
                onKeyDown={onCompactDefRowActivate}
            >
                <td className="cell readonly def-row-compact__name-cell">
                    <input
                        readOnly
                        tabIndex={-1}
                        defaultValue={item.fileName}
                        disabled={!item.local}
                        data-field="fileName"
                        onChange={() => {}}
                    />
                </td>
            </tr>
        );
    }, [selectedDefKey, onCompactDefRowActivate, dKey.KEY]);

    const renderDefTable = useCallback(() => {
        const useCompactRows = defPaneNarrow;
        const showPaneToggle = defPaneNarrow || Boolean(selectedLst) || isLstLoading;
        return (
            <React.Fragment>
                <div className="def-search-row">
                    <div className="def-search-field-wrap">
                        <TextField placeholder="Search here..." fullWidth onChange={onFilterDef} />
                    </div>
                    {showPaneToggle ? (
                        <IconButton
                            onClick={toggleDefPaneNarrow}
                            tooltip={useCompactRows ? 'Expand index list' : 'Shrink index list'}
                            tooltipPosition="top-center"
                        >
                            {useCompactRows ? <ChevronRight /> : <ChevronLeft />}
                        </IconButton>
                    ) : null}
                </div>
                <div ref={defList} className="def-list">
                    <table>
                        <thead>
                            {useCompactRows ? (
                                <tr><th>File</th></tr>
                            ) : (
                                <tr><th>File</th><th>Major type</th><th>Minor type</th><th>Type</th><th aria-label="Actions" /></tr>
                            )}
                        </thead>
                        <tbody>
                            {defView.map(useCompactRows ? renderDefRowCompact : renderDefRow)}
                        </tbody>
                    </table>
                </div>
            </React.Fragment>
        );
    }, [defView, renderDefRow, renderDefRowCompact, onFilterDef, selectedLst, defPaneNarrow, isLstLoading, toggleDefPaneNarrow]);
    
    const renderLstTable = useCallback(() => {
        const hasTable = Boolean(lstView.current);

        let listBody = null;
        if (hasTable) {
            if (process.env.NODE_ENV !== 'production') {
                prettyPrint(gazetteersChanged);
            }
            const totalListWidth = lstTableTotalWidth(columns.current.size);
            const listVersionKey = `${selectedLstVersion}${selectedLst}${columns.current.size}${lstLocalVersion.current}`;
            const headerRow = (
                <React.Fragment>
                    <div className="lst-sticky-leading lst-sticky-leading--header">
                        <div className="lst-controls-header" aria-hidden />
                        <div className="cell cell-header lst-value-header">Value</div>
                    </div>
                    <div className="lst-feature-headers-inline">
                        {Array.from(columns.current, field => (
                            <EditableHeader key={field} defaultValue={field} onChange={onLstFeatureChanged} />
                        ))}
                    </div>
                </React.Fragment>
            );
            const renderVirtualRow = (index) => {
                const item = lstView.current[index];
                if (item == null) {
                    return null;
                }
                const key = item[dKey.KEY];
                const h = LST_ROW_HEIGHT;
                return (
                    <div
                        key={`${key}${selectedLst}${lstLocalVersion.current}`}
                        data-key={key}
                        className="lst-virtual-row"
                        style={{
                            width: totalListWidth,
                            minWidth: totalListWidth,
                            height: h,
                            minHeight: h,
                        }}
                    >
                        <div className="lst-sticky-leading">
                            <div className="controls lst-leading-controls">
                                <DeleteIcon
                                    className="delete"
                                    onClick={onDeleteLstEntry}
                                />
                            </div>
                            <div className="cell lst-value-cell">
                                <input
                                    style={{ height: h }}
                                    defaultValue={item.$value}
                                    data-field="$value"
                                    onChange={onLstCellChanged}
                                />
                            </div>
                        </div>
                        <div className="lst-feature-cells">
                            {Array.from(columns.current, field => (
                                <div key={key + field} className="cell lst-feature-cell">
                                    <input
                                        style={{ height: h }}
                                        className={cx({ invalid: invalidRows.current.has(getValKey(field, key)) })}
                                        defaultValue={item[field]}
                                        data-field={field}
                                        onChange={onLstCellChanged}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                );
            };
            listBody = (
                <React.Fragment>
                    <div>
                        <TextField 
                            placeholder="Search here..." 
                            fullWidth 
                            onChange={onFilterLst} 
                            onKeyPress={(e) => { if (e.charCode === 13) onFilterLst(e, e.target.value); }} 
                        />
                    </div>
                    <div className="lst-list">
                        <GazetteerLstVirtualTable
                            ref={lstList}
                            rowCount={lstView.current.length}
                            rowHeight={LST_ROW_HEIGHT}
                            totalWidth={totalListWidth}
                            listVersionKey={listVersionKey}
                            listDocumentKey={selectedLst || ''}
                            headerRow={headerRow}
                            renderRow={renderVirtualRow}
                        />
                    </div>
                </React.Fragment>
            );
        } else {
            listBody = (
                <div className="empty">
                    <span>Select .lst from left list to edit</span>
                </div>
            );
        }

        return (
            <div className="lst-file-entry__stack">
                <div className="lst-file-entry__content">
                    {listBody}
                </div>
                <GazetteerLstLoadingOverlay active={isLstLoading} />
            </div>
        );
    }, [
        onFilterLst, 
        selectedLstVersion, 
        selectedLst, 
        onDeleteLstEntry,
        onLstCellChanged, 
        dKey.KEY, 
        onLstFeatureChanged, 
        getValKey,
        isLstLoading,
    ]);
    const disableAdminFunctions = !UserPerms.isAdmin();
    
    return (
        <div className="container-fluid gazetteer-editor">
            <div className="paper-block gazetteer-editor-toolbar">
                <div className="toolbar__pane toolbar__pane--left">
                    <span className="toolbar__label">Manage gazetteer index file</span>
                    <RaisedButton label="Add row" primary onClick={onAddDefRow} />
                    <AwaitableBtn><RaisedButton label="Save" primary onClick={onSaveDef} /></AwaitableBtn>
                    <div className="toolbar__paneEnd">
                        <AwaitableBtn>
                            <RaisedButton label='Sync lst' disabled={disableAdminFunctions} onClick={gazetteerService.saveToStaging} primary />
                        </AwaitableBtn>
                        <AwaitableBtn>
                            <RaisedButton label='Deploy' disabled={disableAdminFunctions} onClick={gazetteerService.saveToProduction} secondary />
                        </AwaitableBtn>
                    </div>
                </div>
                <div className="toolbar__divider" aria-hidden="true" />
                <div className="toolbar__pane toolbar__pane--right">
                    <span className="toolbar__label">Manage gazetteer file: {selectedLst}</span>
                    <div className="with-input">
                        <TextField 
                            ref={featureNameRef} 
                            defaultValue="" 
                            errorText={!isNewFeatureNameValid.current && 'invalid feature name'} 
                            style={{ height: 35 }} 
                            underlineStyle={{ bottom: 0 }} 
                            underlineFocusStyle={{ bottom: 0 }} 
                            disabled={!lstView.current}
                        />
                        <RaisedButton label="Add feature" primary onClick={onAddFeature} disabled={!lstView.current} />
                    </div>
                    <RaisedButton label="Add row" primary onClick={onAddLstEntry} disabled={!lstView.current} />
                    <RaisedButton 
                        label={isSavingContent ? 'Saving...' : 'Save'}
                        primary 
                        onClick={onSaveLst} 
                        disabled={isSavingContent || !lstView.current}
                    />
                    <div className="statuses">{lstDuplicatesCount > 0 && <div className="dups">{lstDuplicatesCount} duplicate(s)</div>}</div>
                    <IconMenu
                        iconButtonElement={<IconButton><MoreVertIcon /></IconButton>}
                        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
                        targetOrigin={{ horizontal: 'left', vertical: 'top' }}
                        className="additional-fns"
                        disabled={!lstView.current}
                    >
                        <MenuItem primaryText="Download" leftIcon={<FileDownload />} onClick={onDownloadLst} />
                        <MenuItem primaryText="Dedup" leftIcon={<DeleteSweep />} onClick={onDedupLst} />
                    </IconMenu>
                </div>
            </div>
            <div className="row gazetteer-editor__split-row">
                <div
                    className={cx('def-file-entry', {
                        'def-file-entry--narrow': defPaneNarrow,
                    })}
                >
                    {renderDefTable()}
                </div>
                <div
                    className={cx('lst-file-entry', {
                        'lst-file-entry--wide': defPaneNarrow,
                    })}
                >
                    {renderLstTable()}
                </div>
            </div>
        </div>
    );
};

function EditableHeader(props) {
    const { onChange, defaultValue } = props;
    const session = useRef(Math.random().toFixed(6)).current;

    const onBlur = (e) => {
        if (e.relatedTarget && e.relatedTarget.dataset.session === session) return;
        onChange(defaultValue, e.target.value);
    };

    const onKeyUp = (e) => {
        if (e.key === 'Enter') {
            onChange(defaultValue, e.target.value);
            e.target.blur();
        }
    };

    const onRemoveClicked = () => {
        onChange(defaultValue, null, true);
    };

    return (
        <div key={defaultValue} className="cell cell-header editable">
            <input {...props} key={defaultValue + session} onKeyUp={onKeyUp} onBlur={onBlur} onChange={() => {}} />
            <div className="remove" tabIndex={0} data-session={session} onClick={onRemoveClicked}>&#x274C;</div>
        </div>
    );
}

export default withGazetterContentSave(GazetteerEditorPage);

function prettyPrint(gazetteersChanged) {
    console.group('%c Gazetteer Changes ', 'background: #222; color: #bada55; font-size: 14px; font-weight: bold; border-radius: 3px; padding: 2px;');
    gazetteersChanged.current.forEach((change) => {
        const {
            key, ...rest
        } = change;
        const statusStyle = `color: ${
            change.status === 'new' ? 'green' : change.status === 'remove' ? 'red' : 'orange'
        }; font-weight: bold;`;
        console.log(`%c[${change.status.toUpperCase()}]`, statusStyle, rest);
    });
    console.groupEnd();
}



// WEBPACK FOOTER //
// ./app/components/Gazetteers/Page.jsx